// pages/Yourskills.jsx
import React, { useState, useRef, useEffect } from "react";
import api from "../../api";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Modal,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Snackbar
} from "@mui/material";
import { motion } from "framer-motion";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";
import { getFullImageUrl } from "../../middleware/Imagegetter";
import { getPreviewFileUrl } from "../../middleware/Filehelper";

export default function Skills({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    department: "cse",
    image: null,
    file: null,
  });

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Define sidebar items to pass to Navbar
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Your Skills', 
      path: '/yourskills', 
      action: () => navigate('/yourskills'),
      icon: <SkillsIcon />,
      active: true
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />
    },
  ];

  const departments = ["cse", "ece", "me", "ee", "ce"];

  const projectRefs = {
    cse: useRef(null),
    ece: useRef(null),
    me: useRef(null),
    ee: useRef(null),
    ce: useRef(null),
  };

  const notesRefs = {
    cse: useRef(null),
    ece: useRef(null),
    me: useRef(null),
    ee: useRef(null),
    ce: useRef(null),
  };

  const scroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  const fetchskills = async () => {
    try {
      setLoading(true);
      const response = await api.get("skills/my-skills/");
      // console.log(response.results.data)
      if (!response.results.data) {
        console.error("Invalid data format from backend");
        setSnackbarMessage("Failed to load skills");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const newProjects = response.results.data.filter(
        (item) => item.skill?.toLowerCase() === "project"
      );
      const newNotes = response.results.data.filter(
        (item) => item.skill?.toLowerCase() === "note" || item.skill?.toLowerCase() === "notes"
      );

      setProjects(newProjects);
      setNotes(newNotes);
    } catch (err) {
      console.error("Failed to load skill data:", err);
      setSnackbarMessage("Failed to load skills. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchskills();
  }, []);

const handledownload = (item) => {
  if (!item.pdf) {
    console.error("No PDF available");
    return;
  }

  const previewUrl = getPreviewFileUrl(item.pdf);

  if (!previewUrl) {
    console.error("Invalid PDF URL");
    return;
  }

  // Browser-native preview
  window.open(previewUrl, "_blank", "noopener,noreferrer");
};

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      department: "cse",
      image: null,
      file: null,
    });
  };

  const handleSubmit = async (type) => {
    if (!formData.name) {
      setSnackbarMessage("Title is required");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("description", formData.description);
      payload.append("department", formData.department);
      payload.append("skill", type);
      payload.append("price", "0.00");

      if (formData.image) payload.append("image", formData.image);
      if (formData.file) payload.append("pdf", formData.file);

      const response = await api.post("skills/my-skills/", payload);

      setSnackbarMessage(`${type === "project" ? "Project" : "Note"} added successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      resetForm();

      if (type === "project") {
        setOpenProjectModal(false);
        setProjects([response.data, ...projects]);
      } else {
        setOpenNoteModal(false);
        setNotes([response.data, ...notes]);
      }
    } catch (err) {
      console.error("Error adding skill:", err);
      setSnackbarMessage(err.response?.data?.message || "Failed to add. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (item, type) => {
    setItemToDelete({ id: item.id, type, name: item.name });
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await api.delete(`skills/my-skills/${itemToDelete.id}/`);
      
      if (itemToDelete.type === "project") {
        setProjects(projects.filter(p => p.id !== itemToDelete.id));
      } else {
        setNotes(notes.filter(n => n.id !== itemToDelete.id));
      }
      
      setSnackbarMessage(`${itemToDelete.type === "project" ? "Project" : "Note"} deleted successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error deleting item:", err);
      setSnackbarMessage("Failed to delete. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: embedded ? "auto" : "100vh" }}>
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Only show sidebar on desktop */}
        {!embedded && !isMobile && <GlobalSidebar hideManagement />}
        
        <Box component="main" sx={{ 
          flexGrow: 1, 
          overflowX: "hidden",
          backgroundColor: "#dfdfdfff",
          color: "#000000ff"
        }}>
          <Box sx={{ p: embedded ? { xs: 2, md: 3 } : { xs: 2, md: 4 } }}>
            {!embedded && <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" sx={{ 
                background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", 
                WebkitBackgroundClip: "text", 
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.8rem", md: "2.5rem" }
              }}>
                MY SKILLS
              </Typography>
            </Box>

            {loading && <CircularProgress sx={{ display: "block", mx: "auto" }} />}

            {/* Projects Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ 
                background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", 
                WebkitBackgroundClip: "text", 
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}>
                PROJECTS
              </Typography>
              <Button variant="contained" sx={{ background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } }} onClick={() => setOpenProjectModal(true)} >
                ADD PROJECT
              </Button>
            </Box>

            {departments.map((dept) => {
              const deptProjects = projects.filter((p) => p.department.toLowerCase() === dept);
              if (deptProjects.length === 0) return null;
              return (
                <Box key={dept} mb={6}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      background:
                        "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "block",
                      textAlign: "left",
                    }}
                  >
                    {dept.toUpperCase()} PROJECTS
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => scroll(projectRefs[dept], "left")} sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}><ArrowBackIosNewIcon /></IconButton>
                    <Box ref={projectRefs[dept]} sx={{ display: "flex", gap: 2, overflowX: "auto", flex: 1, scrollBehavior: "smooth", py: 1, px: { xs: 1, sm: 0 }, "&::-webkit-scrollbar": { display: "none" } }}>
                      {deptProjects.map((project) => (
                        <motion.div key={project.id} whileHover={{ scale: 0.95 }} style={{ width: "clamp(240px, 80vw, 280px)", flexShrink: 0 }}>
                          <Card sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.2)", backdropFilter: "blur(15px)", background: "rgba(255, 255, 255, 0.96)", border: "1px solid rgba(255, 255, 255, 0.91)" }}>
                            <CardMedia component="img" height="160" image={getFullImageUrl(project.image)} alt={project.name} onError={(e) => { e.target.src = "/placeholder-image.jpg"; }} />
                            <CardContent>
                              <Typography variant="h6" fontWeight="bold" noWrap>{project.name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{project.description}</Typography>
                              <Typography variant="caption" display="block" mt={1}>Dept: {project.department.toUpperCase()}</Typography>
                              <Typography variant="caption" display="block">By: {project.owner_username}</Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                <Button onClick={() => handledownload(project)} variant="contained" size="small" sx={{ flex: 1, borderRadius: "12px", background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", boxShadow: "0 8px 25px rgba(0,198,255,0.3)", "&:hover": { transform: "scale(1.05)", boxShadow: "0 12px 35px rgba(9, 49, 1, 0.7)" } }}>DOWNLOAD</Button>
                                <IconButton onClick={() => openDeleteDialog(project, "project")} color="error" sx={{ background: "rgba(211, 47, 47, 0.1)", "&:hover": { background: "rgba(211, 47, 47, 0.2)" } }}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </Box>
                    <IconButton onClick={() => scroll(projectRefs[dept], "right")} sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}><ArrowForwardIosIcon /></IconButton>
                  </Box>
                </Box>
              );
            })}

            {/* Notes Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ 
                background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", 
                WebkitBackgroundClip: "text", 
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}>
                NOTES
              </Typography>
              <Button variant="contained" sx={{ background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } }} onClick={() => setOpenNoteModal(true)}>
                ADD NOTES
              </Button>
            </Box>

            {departments.map((dept) => {
              const deptNotes = notes.filter((n) => n.department.toLowerCase() === dept);
              if (deptNotes.length === 0) return null;
              return (
                <Box key={dept} mb={6}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      background:
                        "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "block",
                      textAlign: "left",
                    }}
                  >
                    {dept.toUpperCase()} NOTES
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => scroll(notesRefs[dept], "left")} sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}><ArrowBackIosNewIcon /></IconButton>
                    <Box ref={notesRefs[dept]} sx={{ display: "flex", gap: 2, overflowX: "auto", flex: 1, scrollBehavior: "smooth", py: 1, px: { xs: 1, sm: 0 }, "&::-webkit-scrollbar": { display: "none" } }}>
                      {deptNotes.map((note) => (
                        <motion.div key={note.id} whileHover={{ scale: 0.95 }} style={{ width: "clamp(240px, 80vw, 280px)", flexShrink: 0 }}>
                          <Card sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.2)", backdropFilter: "blur(15px)", background: "rgba(255, 255, 255, 0.89)", border: "1px solid rgba(255, 255, 255, 0.84)" }}>
                            <CardMedia
                              component="img"
                              height="160"
                              image={getFullImageUrl(note.image)}
                              alt={note.name}
                              onError={(e) => { e.target.src = "/placeholder-image.jpg"; }}
                            />
                            <CardContent>
                              <Typography variant="h6" fontWeight="bold">{note.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{note.description}</Typography>
                              <Typography variant="caption" display="block" mt={1}>Dept: {note.department.toUpperCase()}</Typography>
                              <Typography variant="caption" display="block">By: {note.owner_username}</Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                <Button onClick={() => handledownload(note)} variant="contained" size="small" sx={{ flex: 1, borderRadius: "12px", background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", boxShadow: "0 8px 25px rgba(0,198,255,0.3)", "&:hover": { transform: "scale(1.05)", boxShadow: "0 12px 35px rgba(9, 49, 1, 0.7)" } }}>DOWNLOAD</Button>
                                <IconButton onClick={() => openDeleteDialog(note, "note")} color="error" sx={{ background: "rgba(211, 47, 47, 0.1)", "&:hover": { background: "rgba(211, 47, 47, 0.2)" } }}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </Box>
                    <IconButton onClick={() => scroll(notesRefs[dept], "right")} sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}><ArrowForwardIosIcon /></IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Modal */}
      <Modal 
        open={openProjectModal} 
        onClose={() => setOpenProjectModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: 400 },
            maxHeight: '80vh',
            bgcolor: '#fff',
            color: '#000',
            p: 3,
            borderRadius: '16px',
            overflowY: 'auto',
            boxShadow: 24
          }}
        >
          <Typography variant="h6" mb={2}>
            Add Project
          </Typography>
          <TextField
            fullWidth
            label="Title"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Department</InputLabel>
            <Select
              name="department"
              value={formData.department}
              onChange={handleChange}
              label="Department"
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" sx={{ background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)" , mt: 2 }} component="label" fullWidth >
            Upload Image
            <input
              type="file"
              hidden
              name="image"
              accept="image/*"
              onChange={handleChange}
            />
          </Button>
          {formData.image && (
            <Typography variant="caption" display="block">
              Selected: {formData.image.name}
            </Typography>
          )}
          <Button variant="contained" sx={{ background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)" , mt: 2 }} component="label" fullWidth >
            Upload Project File
            <input
              type="file"
              hidden
              name="file"
              accept=".zip,.rar,.7z,.pdf,.doc,.docx"
              onChange={handleChange}
            />
          </Button>
          {formData.file && (
            <Typography variant="caption" display="block">
              Selected: {formData.file.name}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } , mt: 2 }} 
            onClick={() => handleSubmit('project')}
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Project'}
          </Button>
        </Box>
      </Modal>

      {/* Note Modal */}
      <Modal 
        open={openNoteModal} 
        onClose={() => setOpenNoteModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box 
          sx={{
            width: { xs: '90%', sm: 400 },
            maxHeight: '80vh',
            bgcolor: '#fff',
            color: '#000',
            p: 3,
            borderRadius: '16px',
            overflowY: 'auto',
            boxShadow: 24
          }}
        >
          <Typography variant="h6" mb={2}>Add Note</Typography>
          <TextField 
            fullWidth 
            label="Title" 
            name="name" 
            value={formData.name} 
            onChange={handleChange} 
            margin="normal" 
            required 
          />
          <TextField 
            fullWidth 
            label="Description" 
            name="description" 
            value={formData.description} 
            onChange={handleChange} 
            margin="normal" 
            multiline 
            rows={3} 
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Department</InputLabel>
            <Select name="department" value={formData.department} onChange={handleChange} label="Department">
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>{dept.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" sx={{background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } , mt: 2 }}  component="label" fullWidth >
            Upload Image
            <input type="file" hidden name="image" accept="image/*" onChange={handleChange} />
          </Button>
          {formData.image && <Typography variant="caption" display="block">Selected: {formData.image.name}</Typography>}
          <Button variant="contained" sx={{background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } , mt: 2 }} component="label" fullWidth >
            Upload Note File
            <input type="file" hidden name="file" accept=".pdf,.doc,.docx,.txt" onChange={handleChange} />
          </Button>
          {formData.file && <Typography variant="caption" display="block">Selected: {formData.file.name}</Typography>}
          <Button 
            fullWidth 
            variant="contained" 
            sx={{background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", minWidth: { xs: "100%", sm: "auto" } , mt: 2 }}  
             
            onClick={() => handleSubmit('notes')} 
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </Button>
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {!embedded && (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      )}
    </Box>
  );
}